import React, { useMemo, useState } from 'react';

import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import Heading from '@atlaskit/heading';
import SkillIcon from '@atlaskit/icon-lab/core/skill';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { ADFEncoder, ReactRenderer } from '@atlaskit/renderer';
import Spinner from '@atlaskit/spinner/spinner';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';

const createAdfEncoder = () => new ADFEncoder((schema) => new MarkdownTransformer(schema));

let adfEncoder: ReturnType<typeof createAdfEncoder> | undefined;

const getAdfEncoder = () => {
	if (!adfEncoder) {
		adfEncoder = createAdfEncoder();
	}

	return adfEncoder;
};

const Markdown = ({ source }: { source: string }) => {
	const adfDocument = useMemo(() => getAdfEncoder().encode(source), [source]);

	return <ReactRenderer document={adfDocument} />;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max height of the body when collapsed (px). */
const COLLAPSED_MAX_HEIGHT = 200;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = cssMap({
	// Card shell
	card: {
		width: '100%',
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
		isolation: 'isolate',
	},

	// Header
	header: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
	headerTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0',
		flex: 1,
	},

	// Body outer — provides the clip boundary and border
	bodyOuter: {
		position: 'relative',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	// Applied when body is collapsed — clips to max height
	bodyCollapsed: {
		maxHeight: `${COLLAPSED_MAX_HEIGHT}px`,
		overflow: 'hidden',
	},
	// Inner padding
	bodyInner: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	bodyInnerCollapsed: {
		// Extra bottom room so the floating button doesn't overlap last line of text
		paddingBlockEnd: token('space.800'),
	},

	// Fade gradient that sits over the bottom of the collapsed body
	fade: {
		position: 'absolute',
		insetInlineStart: '0',
		insetInlineEnd: '0',
		insetBlockEnd: '0',
		height: '80px',
		backgroundImage: `linear-gradient(to bottom, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
	},

	// Shared "pill" surface (overlay background + shadow) for the
	// "Show more" / "Show less" toggle buttons.
	toggleButtonSurface: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	// "Show more" button — floats centered over the fade
	floatingShowMore: {
		position: 'absolute',
		insetInlineStart: '50%',
		insetBlockEnd: token('space.150'),
		transform: 'translateX(-50%)',
		zIndex: 1,
	},
	showLessWrapper: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.150'),
	},

	// Section label (e.g. "Skill name", "Description")
	sectionLabel: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
	},

	// Footer
	footer: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
});

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const Section = ({ label, children }: { label: MessageDescriptor; children: React.ReactNode }) => (
	<Stack space="space.075">
		<Box xcss={styles.sectionLabel}>
			<FormattedMessage {...label} />
		</Box>
		<Box>{children}</Box>
	</Stack>
);

// ---------------------------------------------------------------------------
// Plan / draft state (expandable)
// ---------------------------------------------------------------------------

const CustomSkillCardPlan = ({
	displayName,
	helpText,
	description,
	instructions,
	headerLabel,
	confirmLabel,
	isDisabled = false,
	isSubmitting = false,
	isLoadingDetails = false,
	onConfirm,
	confirmSlot,
}: {
	displayName: string;
	helpText?: string;
	description: string;
	instructions: string;
	headerLabel: MessageDescriptor;
	confirmLabel: MessageDescriptor;
	isDisabled?: boolean;
	isSubmitting?: boolean;
	isLoadingDetails?: boolean;
	onConfirm?: () => void;
	confirmSlot?: React.ReactNode;
}): React.JSX.Element => {
	const intl = useIntl();

	// Controls whether the whole card body+footer is visible
	const [isCardCollapsed, setIsCardCollapsed] = useState(false);
	// Controls whether the body content is height-limited (Show more)
	const [isBodyExpanded, setIsBodyExpanded] = useState(false);

	const toggleCard = () => setIsCardCollapsed((v) => !v);
	const showMore = () => setIsBodyExpanded(true);
	const showLess = () => setIsBodyExpanded(false);

	return (
		<Box xcss={styles.card} testId="custom-skill-card">
			{/* ── Header ── */}
			<Box xcss={styles.header}>
				<Box xcss={styles.headerTitle}>
					<Tile size="small" label="" backgroundColor="transparent" hasBorder>
						<SkillIcon label="" />
					</Tile>
					<Heading size="xsmall" as="h3">
						<FormattedMessage {...headerLabel} />
					</Heading>
					{isLoadingDetails && (
						<Spinner
							size="small"
							label={intl.formatMessage(messages.loadingDetailsLabel)}
							testId="custom-skill-card-details-spinner"
						/>
					)}
				</Box>
				{!isLoadingDetails && (
					<IconButton
						appearance="subtle"
						spacing="compact"
						shape="circle"
						icon={isCardCollapsed ? ChevronDownIcon : ChevronUpIcon}
						label={intl.formatMessage(
							isCardCollapsed ? messages.expandLabel : messages.collapseLabel,
						)}
						onClick={toggleCard}
						testId="custom-skill-card-toggle"
					/>
				)}
			</Box>

			{/* ── Body + Footer — hidden when the card is collapsed or while the latest details load ── */}
			{!isCardCollapsed && !isLoadingDetails && (
				<>
					{/* Body with optional height clamp + fade + floating "Show more" */}
					<Box xcss={styles.bodyOuter}>
						<Box xcss={isBodyExpanded ? undefined : styles.bodyCollapsed}>
							<Box xcss={cx(styles.bodyInner, !isBodyExpanded && styles.bodyInnerCollapsed)}>
								<Stack space="space.200">
									{displayName && (
										<Section label={messages.skillNameLabel}>
											<Text color="color.text">{displayName}</Text>
										</Section>
									)}
									{helpText && fg('chat_skill_helptext_enabled') && (
										<Section label={messages.summaryLabel}>
											<Text color="color.text">{helpText}</Text>
										</Section>
									)}
									{description && (
										<Section label={messages.descriptionLabel}>
											<Text color="color.text">{description}</Text>
										</Section>
									)}
									{instructions && (
										<Section label={messages.instructionsLabel}>
											<Markdown source={instructions} />
										</Section>
									)}
								</Stack>
							</Box>
						</Box>

						{isBodyExpanded && (
							<Box xcss={styles.showLessWrapper}>
								<Box xcss={styles.toggleButtonSurface}>
									<Button
										appearance="default"
										spacing="compact"
										onClick={showLess}
										testId="custom-skill-card-show-less"
									>
										<FormattedMessage {...messages.showLessLabel} />
									</Button>
								</Box>
							</Box>
						)}

						{/* Fade + floating "Show more" only when body is collapsed */}
						{!isBodyExpanded && (
							<>
								<Box xcss={styles.fade} />
								<Box xcss={cx(styles.toggleButtonSurface, styles.floatingShowMore)}>
									<Button
										appearance="default"
										spacing="compact"
										onClick={showMore}
										testId="custom-skill-card-show-more"
									>
										<FormattedMessage {...messages.showMoreLabel} />
									</Button>
								</Box>
							</>
						)}
					</Box>

					{/* Footer */}
					<Box xcss={styles.footer}>
						<Flex justifyContent="end" alignItems="center" gap="space.100">
							{confirmSlot ?? (
								<Button
									appearance="primary"
									isDisabled={isDisabled || isSubmitting}
									isLoading={isSubmitting}
									onClick={onConfirm}
									testId="custom-skill-card-confirm"
								>
									<FormattedMessage {...confirmLabel} />
								</Button>
							)}
						</Flex>
					</Box>
				</>
			)}
		</Box>
	);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type CustomSkillCardTool = {
	id: string;
	source: string;
	type: string;
};

export type CustomSkillCardProps = {
	/** Human-readable display name shown in the card body. */
	displayName: string;
	/**
	 * Concise, user-facing summary of the skill's purpose, shown as "Summary".
	 * Optional — the section is omitted when absent.
	 */
	helpText?: string;
	/** Description of what this skill does. */
	description: string;
	/** Instructions for the skill, rendered as Markdown. */
	instructions: string;
	/** Header label shown above the skill details. */
	headerLabel: MessageDescriptor;
	/** Label for the primary confirm action. */
	confirmLabel: MessageDescriptor;
	/** Disables footer actions. */
	isDisabled?: boolean;
	/** Shows the confirm action as submitting. */
	isSubmitting?: boolean;
	/** Shows a spinner next to the card title while the latest skill details load. */
	isLoadingDetails?: boolean;
	/** Called when the user clicks the confirm button. */
	onConfirm?: () => void;
	/**
	 * Custom footer action. When provided, it replaces the default confirm
	 * button — used to render the "Edit" modal trigger.
	 */
	confirmSlot?: React.ReactNode;
};

export const CustomSkillCard = ({
	displayName,
	helpText,
	description,
	instructions,
	headerLabel,
	confirmLabel,
	isDisabled,
	isSubmitting,
	isLoadingDetails,
	onConfirm,
	confirmSlot,
}: CustomSkillCardProps): React.JSX.Element => {
	return (
		<CustomSkillCardPlan
			displayName={displayName}
			helpText={helpText}
			description={description}
			instructions={instructions}
			headerLabel={headerLabel}
			confirmLabel={confirmLabel}
			isDisabled={isDisabled}
			isSubmitting={isSubmitting}
			isLoadingDetails={isLoadingDetails}
			onConfirm={onConfirm}
			confirmSlot={confirmSlot}
		/>
	);
};
